import { MapPin, Plus } from 'lucide-react';
import { useState } from 'react';
import { MAX_FILE_SIZE_MB } from '../constants';
import { GeoPoint } from '../types';
import { formatFileSize, showErrorToast, showSuccessToast } from '../utils';
import { EditablePointRow } from './EditablePointRow';
import { ManualCoordinateModal } from './ManualCoordinateModal';

interface GeotraceInputProps {
  disabled: boolean;
}

export const GeotraceInput = ({ disabled }: GeotraceInputProps) => {
  const [points, setPoints] = useState<GeoPoint[]>([]);
  const [showManualModal, setShowManualModal] = useState(false);

  const addPointFromGPS = () => {
    if (disabled) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setPoints((p) => [...p, { lat: pos.coords.latitude, lng: pos.coords.longitude }]),
      () => {
        showErrorToast('Could not get location. Please try manual entry.');
      },
    );
  };

  const addPointManually = (lat: number, lng: number) => {
    setPoints((p) => [...p, { lat, lng }]);
    showSuccessToast('Point added successfully!');
  };

  const updatePoint = (index: number, lat: number, lng: number) => {
    setPoints((p) => p.map((pt, i) => (i === index ? { lat, lng } : pt)));
  };

  const deletePoint = (index: number) => {
    setPoints((p) => p.filter((_, i) => i !== index));
  };

  // Calculate payload size estimate
  const payloadSize = new Blob([JSON.stringify(points)]).size;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <MapPin size={18} className="text-blue-600" />
        <span className="text-sm text-blue-800 font-medium">Geotrace – Capture route points</span>
      </div>

      {/* Action buttons */}
      {!disabled && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={addPointFromGPS}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-[#C0275E] text-white rounded-lg text-xs font-semibold hover:bg-[#a01f4e] transition-colors"
          >
            <MapPin size={14} />
            Add GPS Point
          </button>
          <button
            type="button"
            onClick={() => setShowManualModal(true)}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 border border-[#C0275E] text-[#C0275E] rounded-lg text-xs font-semibold hover:bg-pink-50 transition-colors"
          >
            <Plus size={14} />
            Add Manually
          </button>
        </div>
      )}

      {/* Points list */}
      {points.length > 0 && (
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {points.map((p, i) => (
            <EditablePointRow
              key={i}
              point={p}
              index={i}
              onUpdate={updatePoint}
              onDelete={deletePoint}
              disabled={disabled}
            />
          ))}
        </div>
      )}

      {/* Payload size warning */}
      {points.length > 0 && (
        <div className="flex items-center justify-between text-xs text-gray-500 px-1">
          <span>
            {points.length} point{points.length !== 1 ? 's' : ''} captured
          </span>
          <span>Payload: {formatFileSize(payloadSize)}</span>
        </div>
      )}

      {payloadSize > MAX_FILE_SIZE_MB * 1024 * 1024 && (
        <p className="text-xs text-red-500 px-1">
          ⚠️ Payload exceeds {MAX_FILE_SIZE_MB}MB limit. Please reduce points.
        </p>
      )}

      {points.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-2">No points captured yet</p>
      )}

      {/* Manual coordinate modal */}
      <ManualCoordinateModal
        isOpen={showManualModal}
        onClose={() => setShowManualModal(false)}
        onAdd={addPointManually}
      />
    </div>
  );
};
